import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Github,
  Linkedin,
  Twitter,
  Mail,
  Phone,
  MapPin,
  Heart,
  ExternalLink,
  ArrowUp,
} from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    {
      name: "GitHub",
      url: "https://github.com/t-boye",
      icon: Github,
      color: "#181717",
    },
    {
      name: "LinkedIn",
      url: "https://www.linkedin.com/in/tboye",
      icon: Linkedin,
      color: "#0A66C2",
    },
    // {
    //   name: "Twitter",
    //   url: "https://twitter.com/yourusername",
    //   icon: Twitter,
    //   color: "#1DA1F2",
    // },
    {
      name: "Email",
      url: "mailto:emmanuelboye1957@gmail.com",
      icon: Mail,
      color: "#EA4335",
    },
  ];

  const quickLinks = [
    { name: "About", href: "#about", type: "scroll" },
    { name: "Projects", href: "#projects", type: "scroll" },
    { name: "Blog", href: "/blog", type: "route" },
    { name: "Experience", href: "#experience", type: "scroll" },
    { name: "Contact", href: "#contact", type: "scroll" },
  ];

  const services = [
    { name: "Web/Mobile App Development", href: "#" },
    { name: "Pentesting", href: "#" },
    { name: "Tech Consulting", href: "#" },
    { name: "AI & Automation Solutions", href: "#" },
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId.replace("#", ""));
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer className="bg-gray-900 dark:bg-black text-white relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-500 rounded-full filter blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-accent-500 rounded-full filter blur-3xl" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Main Footer Content */}
        <div className="pt-16 pb-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* Brand Section */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h3 className="text-2xl font-heading font-bold mb-4 text-primary-400">
                  @tboye.tech
                </h3>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  Software/AI Engineer passionate about
                  creating exceptional digital experiences that inspire and
                  engage users.
                </p>

                {/* Contact Info */}
                <div className="space-y-3">
                  <div className="flex items-center text-gray-300">
                    <MapPin size={16} className="mr-3 text-primary-400" />
                    <span className="text-sm">Abeka, Accra, Ghana</span>
                  </div>
                  <div className="flex items-center text-gray-300">
                    <Phone size={16} className="mr-3 text-primary-400" />
                    <a
                      href="https://wa.me/233593501488"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm hover:text-primary-400 transition-colors"
                    >
                      +233 59 350 1488 · WhatsApp
                    </a>
                  </div>
                  <div className="flex items-center text-gray-300">
                    <Mail size={16} className="mr-3 text-primary-400" />
                    <span className="text-sm">emmanuelboye1957@gmail.com</span>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Quick Links */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <h4 className="text-lg font-semibold mb-6 text-white">
                  Quick Links
                </h4>
                <ul className="space-y-3">
                  {quickLinks.map((link) => (
                    <li key={link.name}>
                      {link.type === "route" ? (
                        <Link
                          to={link.href}
                          className="text-gray-300 hover:text-primary-400 transition-colors duration-200 text-sm group"
                        >
                          <span className="flex items-center">
                            {link.name}
                            <ExternalLink size={12} className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </span>
                        </Link>
                      ) : (
                        <button
                          onClick={() => scrollToSection(link.href)}
                          className="text-gray-300 hover:text-primary-400 transition-colors duration-200 text-sm group"
                        >
                          <span className="flex items-center">
                            {link.name}
                            <ExternalLink size={12} className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </span>
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* Services */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <h4 className="text-lg font-semibold mb-6 text-white">
                  Services
                </h4>
                <ul className="space-y-3">
                  {services.map((service) => (
                    <li key={service.name}>
                      <a
                        href={service.href}
                        className="text-gray-300 hover:text-primary-400 transition-colors duration-200 text-sm group"
                      >
                        <span className="flex items-center">
                          {service.name}
                          <ExternalLink
                            size={12}
                            className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          />
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* Newsletter & Social */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <h4 className="text-lg font-semibold mb-6 text-white">
                  Stay Connected
                </h4>

                {/* Newsletter Signup */}
                <div className="mb-6">
                  <p className="text-gray-300 text-sm mb-4">
                    Get updates on my latest projects and tech insights.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="email"
                      placeholder="Enter your email"
                      className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                    />
                    <motion.button
                      className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium text-sm"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Subscribe
                    </motion.button>
                  </div>
                </div>

                {/* Social Links */}
                <div>
                  <p className="text-sm text-gray-300 mb-4">
                    Follow me on social media:
                  </p>
                  <div className="flex space-x-4">
                    {socialLinks.map((social) => {
                      const Icon = social.icon;
                      return (
                        <motion.a
                          key={social.name}
                          href={social.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-all duration-300 group"
                          whileHover={{ scale: 1.1, y: -2 }}
                          whileTap={{ scale: 0.9 }}
                          style={{
                            "--hover-color": social.color,
                          }}
                        >
                          <Icon
                            size={18}
                            className="text-gray-300 group-hover:text-primary-400 transition-colors"
                          />
                        </motion.a>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              {/* Copyright */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="flex items-center text-gray-400 text-sm mb-4 md:mb-0"
              >
                <span>© {currentYear} @tboye.tech Made with</span>
                <Heart size={14} className="mx-1 text-red-400 animate-pulse" />
                <span>in Ghana</span>
              </motion.div>

              {/* Back to Top */}
              <motion.button
                onClick={scrollToTop}
                className="flex items-center text-gray-400 hover:text-primary-400 transition-colors duration-200 text-sm group"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="mr-2">Back to top</span>
                <ArrowUp
                  size={16}
                  className="group-hover:-translate-y-1 transition-transform"
                />
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-primary-500 opacity-50" />
    </footer>
  );
};

export default Footer;
